// <Leaderboard kind="spades" worldId={…} />: the ranking for one game, with
// "This world / Everyone" and "Week / Month / All time" tabs. Used in game
// overlays (lobby + game over), as a modal from the join prompt, and on the
// dashboard's Games tab (no worldId → Everyone only). The VR version is
// VRLeaderboardPanel in kit/vrKit.tsx.

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLeaderboard, RANGE_LABELS, type LbOrder, type LbRange } from './leaderboardData';
import { getGameDef } from './registry';
import './kit/gameUi.css';
import './Leaderboard.css';

export function Leaderboard({ kind, worldId, order, limit = 10, refreshKey, compact = false, title }: {
  kind: string;
  /** Offer the "This world" tab (default tab when given). */
  worldId?: string | null;
  /** Default: the game's registry ranking. */
  order?: LbOrder;
  limit?: number;
  /** Change it to refetch (e.g. the finished game's id). */
  refreshKey?: unknown;
  compact?: boolean;
  title?: string | false;
}) {
  const def = getGameDef(kind);
  const ranking: LbOrder = order ?? def?.ranking ?? 'wins';
  const [scope, setScope] = useState<'world' | 'all'>(worldId ? 'world' : 'all');
  const [range, setRange] = useState<LbRange>('all');
  const { rows, loading, error } = useLeaderboard(kind, scope === 'world' && worldId ? worldId : null, range, ranking, limit, refreshKey ?? 0);
  const pts = ranking === 'points';

  return (
    <div className={`gx-lb ${compact ? 'gx-lb--compact' : ''}`}>
      {title !== false && <div className="gx-lb-title">{title ?? `${def?.label ?? kind} leaderboard`}</div>}
      <div className="gx-lb-tabs">
        {worldId && (
          <div className="gx-lb-seg" role="tablist" aria-label="Whose scores">
            <button role="tab" aria-selected={scope === 'world'} className={scope === 'world' ? 'is-on' : ''} onClick={() => setScope('world')}>This world</button>
            <button role="tab" aria-selected={scope === 'all'} className={scope === 'all' ? 'is-on' : ''} onClick={() => setScope('all')}>Everyone</button>
          </div>
        )}
        <div className="gx-lb-seg" role="tablist" aria-label="Time range">
          {(['week', 'month', 'all'] as LbRange[]).map((r) => (
            <button key={r} role="tab" aria-selected={range === r} className={range === r ? 'is-on' : ''} onClick={() => setRange(r)}>
              {r === 'all' ? 'All time' : r === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
      </div>
      {error ? <p className="gx-lb-empty">{error}</p>
        : loading && !rows.length ? <p className="gx-lb-empty">Loading…</p>
        : !rows.length ? <p className="gx-lb-empty">No games yet {range === 'all' ? '' : RANGE_LABELS[range].toLowerCase()} — be the first.</p>
        : (
          <table className="gx-lb-table">
            <thead>
              <tr>
                <th className="gx-lb-rank">#</th>
                <th className="gx-lb-name">Player</th>
                {pts && <th>Points</th>}
                <th>W</th>
                <th>L</th>
                {!compact && <th>Games</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id} className={r.is_me ? 'is-me' : ''}>
                  <td className="gx-lb-rank">{r.rank <= 3 ? ['🥇', '🥈', '🥉'][r.rank - 1] : r.rank}</td>
                  <td className="gx-lb-name">{r.name}{r.is_me ? ' (you)' : ''}</td>
                  {pts && <td><b>{r.points}</b></td>}
                  <td>{pts ? r.wins : <b>{r.wins}</b>}</td>
                  <td>{r.losses}</td>
                  {!compact && <td>{r.games}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  );
}

/** The leaderboard in a dialog over the viewer (from a game's prompt / lobby). */
export function LeaderboardModal({ kind, worldId, onClose }: { kind: string; worldId?: string | null; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const def = getGameDef(kind);
  const host = (document.querySelector('.gx-viewer') as HTMLElement | null) ?? document.body;
  return createPortal(
    <div className="gx-sp-rules-backdrop" onClick={onClose}>
      <div className="gx-sp-rules gx-lb-modal" role="dialog" aria-modal="true" aria-label={`${def?.label ?? kind} leaderboard`} onClick={(e) => e.stopPropagation()}>
        <div className="gx-sp-rules-head">
          <h2>{def?.glyph && <span className="gx-sp-spade">{def.glyph}</span>} {def?.label ?? kind} leaderboard</h2>
          <button ref={closeRef} className="gx-sp-btn gx-sp-btn--ghost" onClick={onClose} aria-label="Close">Close</button>
        </div>
        <Leaderboard kind={kind} worldId={worldId} title={false} limit={20} />
      </div>
    </div>,
    host,
  );
}

export default Leaderboard;
